/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ActivityIndicator, Image, StyleSheet, Text, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { colors } from '../../../shared/theme/colors';
import { typography } from '../../../shared/theme/typography';
import { TicketButton } from '../../../shared/ticket/TicketButton';

type Props = {
  logoUrl: string | null;
  organizationName: string;
  canEdit: boolean;
  uploading: boolean;
  onChangeLogo: () => void;
  onRemoveLogo: () => void;
};

export function OrganizationLogoSection({
  logoUrl,
  organizationName,
  canEdit,
  uploading,
  onChangeLogo,
  onRemoveLogo,
}: Props) {
  const initial = organizationName.trim().charAt(0).toUpperCase() || 'N';
  const showImage = Boolean(logoUrl);

  return (
    <View style={styles.wrap} testID="organization.logo.section">
      <View style={styles.row}>
        <View style={styles.previewShell}>
          {uploading ? (
            <ActivityIndicator size="small" color={ticketColors.gold} />
          ) : showImage ? (
            <Image source={{ uri: logoUrl! }} style={styles.previewImage} resizeMode="cover" />
          ) : (
            <Text style={styles.previewInitial}>{initial}</Text>
          )}
        </View>
        <View style={styles.copy}>
          <Text style={styles.label}>Logo del negocio</Text>
          <Text style={styles.hint}>
            Las sucursales que no tienen logo propio lo heredan de acá. También lo ves en el panel del dueño.
          </Text>
          {canEdit ? (
            <View style={styles.actions}>
              <TicketButton
                variant="gold"
                label={showImage ? 'Cambiar logo' : 'Subir logo'}
                onPress={onChangeLogo}
                disabled={uploading}
                testID="organization.logo.change"
              />
              {showImage ? (
                <TicketButton
                  variant="ghost"
                  label="Quitar"
                  onPress={onRemoveLogo}
                  disabled={uploading}
                  testID="organization.logo.remove"
                />
              ) : null}
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
}

const PREVIEW = 72;

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  previewShell: {
    width: PREVIEW,
    height: PREVIEW,
    borderRadius: 14,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
  },
  previewImage: {
    width: PREVIEW,
    height: PREVIEW,
  },
  previewInitial: {
    ...typography.title3,
    fontWeight: '800',
    color: colors.surface,
  },
  copy: {
    flex: 1,
    minWidth: 0,
    gap: spacing.xs,
  },
  label: {
    ...typography.caption,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  hint: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
});
